import { useMemo } from "react";
import { useProfiles } from "@/hooks/useProfiles";
import type { HomeScreenLayout, IconItem, IconKind, SmplifyExtension } from "./types";
import { loadRecentWebClips, type RecentWebClip } from "./webclipRecents";

export interface WebClipSuggestion {
  /** Unique key for React lists. */
  key: string;
  url: string;
  displayName?: string;
  /** Resolved favicon URL captured at add-time, when available. */
  icon?: string;
  /** Persisted favicon-vs-generic verdict, when known. */
  iconKind?: IconKind;
  /** Where this suggestion was sourced from — used for the legend chip. */
  sources: Array<"current" | "saved" | "local">;
  /** ms since epoch — only set when the suggestion came from local recents.
   *  Used by the library panel's "Recently added" sort. */
  addedAt?: number;
}

const HOME_SCREEN_IDENTIFIER = "com.apple.homescreenlayout";

/** Walk an Apple-shape layout (Pages + Dock + nested folders) and yield WebClips. */
function* walkWebClips(layout: HomeScreenLayout | null | undefined): Generator<Extract<IconItem, { Type: "WebClip" }>> {
  if (!layout) return;
  function* walkItems(items: IconItem[] | undefined): Generator<Extract<IconItem, { Type: "WebClip" }>> {
    if (!Array.isArray(items)) return;
    for (const it of items) {
      if (!it || typeof it !== "object") continue;
      if (it.Type === "WebClip" && typeof it.URL === "string" && it.URL) {
        yield it;
      } else if (it.Type === "Folder" && Array.isArray(it.Pages)) {
        for (const page of it.Pages) yield* walkItems(page);
      }
    }
  }
  if (Array.isArray(layout.Pages)) {
    for (const page of layout.Pages) yield* walkItems(page);
  }
  yield* walkItems(layout.Dock);
}

/** Pull web clips from a Smplify extension (both iPhone & iPad variants). */
function* walkExtension(ext: SmplifyExtension | null | undefined): Generator<Extract<IconItem, { Type: "WebClip" }>> {
  if (!ext) return;
  for (const ff of ["iphone", "ipad"] as const) {
    yield* walkWebClips(ext.variants?.[ff]);
  }
}

/**
 * Read every saved configuration profile and yield its homescreen WebClips,
 * preferring the richer `x-smplify` shape (which preserves IconKind) but
 * falling back to the top-level Apple shape when the extension is missing.
 */
function* walkSavedProfiles(profiles: Array<{ payloads: Array<{ payloadId: string; configValues: Record<string, unknown> }> }>): Generator<Extract<IconItem, { Type: "WebClip" }>> {
  for (const profile of profiles ?? []) {
    for (const payload of profile.payloads ?? []) {
      if (payload.payloadId !== HOME_SCREEN_IDENTIFIER) continue;
      const cfg = payload.configValues as Record<string, unknown> | null | undefined;
      if (!cfg || typeof cfg !== "object") continue;
      const ext = (cfg as { "x-smplify"?: SmplifyExtension })["x-smplify"];
      if (ext && ext.variants) {
        yield* walkExtension(ext);
      } else {
        // Legacy: only the Apple shape was saved; IconKind won't be known here.
        yield* walkWebClips(cfg as unknown as HomeScreenLayout);
      }
    }
  }
}

/** Normalize a URL for dedupe — same-origin clips with different trailing
 *  slashes / hashes should collapse to one suggestion. Falls back to the
 *  trimmed raw string when URL parsing fails so we never drop entries. */
function dedupeKey(url: string): string {
  const trimmed = url.trim();
  try {
    const u = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
    // Lowercase host but keep the path verbatim — `/dashboard` and `/admin`
    // really are different clips even on the same site.
    return `${u.protocol}//${u.host.toLowerCase()}${u.pathname}${u.search}`.replace(/\/$/, "");
  } catch {
    return trimmed.toLowerCase();
  }
}

interface MergeInput {
  /** Live editor extension (covers both iPhone & iPad variants). */
  ext?: SmplifyExtension | null;
  saved: Array<Extract<IconItem, { Type: "WebClip" }>>;
  local: RecentWebClip[];
}

/**
 * Merge the three sources (current layout / saved configs / local recents)
 * into one deduped, freshness-ordered list. Exported for unit testing.
 *
 * Dedupe rule:
 *   - Keyed by a normalized URL (lowercase host, trimmed trailing slash).
 *   - Each occurrence contributes its `source` tag so the UI can show
 *     "Already on this layout", etc.
 *   - For overlapping fields (DisplayName/Icon/IconKind) we prefer the
 *     entry that *has* the field, in priority order: local recents (most
 *     recently confirmed) → current layout → saved configs.
 */
export function mergeWebClipSuggestions({ ext, saved, local }: MergeInput): WebClipSuggestion[] {
  const map = new Map<string, WebClipSuggestion>();

  const upsert = (
    url: string,
    fields: Pick<WebClipSuggestion, "displayName" | "icon" | "iconKind">,
    source: WebClipSuggestion["sources"][number],
    addedAt?: number,
  ) => {
    const key = dedupeKey(url);
    const existing = map.get(key);
    if (existing) {
      // Don't overwrite a previously-set field; only fill gaps. This makes
      // source-priority deterministic regardless of insertion order.
      if (!existing.displayName && fields.displayName) existing.displayName = fields.displayName;
      if (!existing.icon && fields.icon) existing.icon = fields.icon;
      if (!existing.iconKind && fields.iconKind) existing.iconKind = fields.iconKind;
      if (!existing.sources.includes(source)) existing.sources.push(source);
      if (addedAt != null && (existing.addedAt == null || addedAt > existing.addedAt)) {
        existing.addedAt = addedAt;
      }
      return;
    }
    map.set(key, {
      key,
      url,
      displayName: fields.displayName,
      icon: fields.icon,
      iconKind: fields.iconKind,
      sources: [source],
      addedAt,
    });
  };

  // Highest priority: local recents (most recently confirmed by the admin).
  for (const r of local) {
    upsert(r.url, { displayName: r.displayName, icon: r.icon, iconKind: r.iconKind }, "local", r.addedAt);
  }
  // Then: current layout — surfaces clips already in iPhone/iPad/folders.
  for (const c of walkExtension(ext)) {
    upsert(c.URL, { displayName: c.DisplayName, icon: c.Icon, iconKind: c.IconKind }, "current");
  }
  // Then: every saved profile in the tenant.
  for (const c of saved) {
    upsert(c.URL, { displayName: c.DisplayName, icon: c.Icon, iconKind: c.IconKind }, "saved");
  }

  return Array.from(map.values());
}

/**
 * React hook: returns a deduped list of "previously added Web Clips" merged
 * from the live layout extension, all saved configuration profiles, and the
 * browser's local-recents store. Each entry is safe to drop onto the canvas
 * (LibraryEntry contract) and carries a persisted IconKind when known.
 */
export function useRecentWebClips(ext: SmplifyExtension | null | undefined): WebClipSuggestion[] {
  const { profiles } = useProfiles();
  const savedClips = useMemo(() => Array.from(walkSavedProfiles(profiles as never)), [profiles]);

  // Re-read the local store on every render — the AddByBundleIdDialog writes
  // to it synchronously, and the cost is negligible for a list capped at 50.
  const local = useMemo(() => loadRecentWebClips(), [profiles, ext]);

  return useMemo(
    () => mergeWebClipSuggestions({ ext, saved: savedClips, local }),
    [ext, savedClips, local],
  );
}
